import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Save, Plus, Trash2, Edit3, Eye, EyeOff } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface AboutContent {
  id?: string;
  section: string;
  content: any;
  updated_at?: string;
}

interface StatItem {
  icon: string;
  value: string;
  label: string;
}

interface ValueItem {
  icon: string;
  title: string;
  description: string;
}

interface TimelineItem {
  year: string;
  title: string;
  description: string;
}

const AboutEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const { toast } = useToast();

  // Main content states
  const [headerTitle, setHeaderTitle] = useState("About UK Model Portfolios");
  const [headerDescription, setHeaderDescription] = useState("");
  const [storyTitle, setStoryTitle] = useState("Our Story");
  const [storyParagraphs, setStoryParagraphs] = useState<string[]>([]);
  const [valuesTitle, setValuesTitle] = useState("Our Values");
  const [valuesDescription, setValuesDescription] = useState("");
  const [timelineTitle, setTimelineTitle] = useState("Our Journey");
  const [timelineDescription, setTimelineDescription] = useState("");
  const [regulatoryTitle, setRegulatoryTitle] = useState("Regulatory & Compliance");
  const [regulatoryDescription, setRegulatoryDescription] = useState("");
  const [ctaTitle, setCtaTitle] = useState("Ready to Start Your Investment Journey?");
  const [ctaDescription, setCtaDescription] = useState("");

  // Dynamic content states
  const [stats, setStats] = useState<StatItem[]>([]);
  const [values, setValues] = useState<ValueItem[]>([]);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);

  useEffect(() => {
    fetchAboutContent();
  }, []);

  const fetchAboutContent = async () => {
    try {
      setLoading(true);
      
      // Check if we have a custom content management table
      const { data: contentData, error: contentError } = await supabase
        .from('service_page_settings')
        .select('*')
        .like('key', 'about_%');

      if (contentError && !contentError.message.includes('does not exist')) {
        console.error('Error fetching about content:', contentError);
      }

      // Load default content if no custom content exists
      loadDefaultContent();
      
      if (contentData && contentData.length > 0) {
        // Override with custom content where available
        contentData.forEach(item => {
          const value = item.value;
          switch (item.key) {
            case 'about_header_title':
              setHeaderTitle(value);
              break;
            case 'about_header_description':
              setHeaderDescription(value);
              break;
            case 'about_story_title':
              setStoryTitle(value);
              break;
            case 'about_story_paragraphs':
              setStoryParagraphs(JSON.parse(value));
              break;
            case 'about_values_title':
              setValuesTitle(value);
              break;
            case 'about_values_description':
              setValuesDescription(value);
              break;
            case 'about_timeline_title':
              setTimelineTitle(value);
              break;
            case 'about_timeline_description':
              setTimelineDescription(value);
              break;
            case 'about_regulatory_title':
              setRegulatoryTitle(value);
              break;
            case 'about_regulatory_description':
              setRegulatoryDescription(value);
              break;
            case 'about_cta_title':
              setCtaTitle(value);
              break;
            case 'about_cta_description':
              setCtaDescription(value);
              break;
            case 'about_stats':
              setStats(JSON.parse(value));
              break;
            case 'about_values':
              setValues(JSON.parse(value));
              break;
            case 'about_timeline':
              setTimeline(JSON.parse(value));
              break;
          }
        });
      }
    } catch (error) {
      console.error('Error fetching about content:', error);
      toast({
        title: "Error",
        description: "Failed to load about page content",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultContent = () => {
    setHeaderDescription("We are a leading UK investment management firm dedicated to helping individuals and families achieve their financial goals through professional portfolio management.");
    
    setStoryParagraphs([
      "Founded in 2009, Ascot Wealth Management Ltd. (trading as Cape Berkshire Asset Management) was born from a simple belief: professional investment management should be accessible to all UK investors, not just the ultra-wealthy.",
      "Our founders, with decades of combined experience at leading investment banks and wealth management firms, recognized that individual investors deserved the same level of professional portfolio management as institutional clients.",
      "Today, we manage over £250 million for more than 500 clients across the UK, maintaining our commitment to transparency, performance, and personalized service that has defined us from the beginning."
    ]);
    
    setValuesDescription("The principles that guide every decision we make and every relationship we build.");
    setTimelineDescription("Key milestones in our growth and development as a leading UK investment firm.");
    setRegulatoryDescription("Your investments are protected by the highest regulatory standards.");
    setCtaDescription("Join hundreds of satisfied clients who trust us with their financial future. Book a consultation to learn how we can help you achieve your goals.");
    
    setStats([
      { icon: "Users", value: "500+", label: "Satisfied Clients" },
      { icon: "TrendingUp", value: "£250M+", label: "Assets Under Management" },
      { icon: "Award", value: "15+", label: "Years Experience" },
      { icon: "Shield", value: "100%", label: "FCA Regulated" }
    ]);
    
    setValues([
      { icon: "Target", title: "Client-Focused", description: "Every decision we make is guided by what's best for our clients' long-term financial success." },
      { icon: "Shield", title: "Transparent", description: "We believe in complete transparency in our fees, processes, and investment approach." },
      { icon: "TrendingUp", title: "Performance-Driven", description: "Our focus is on delivering consistent, risk-adjusted returns that meet your objectives." },
      { icon: "Users", title: "Partnership Approach", description: "We build long-term relationships based on trust, communication, and mutual respect." }
    ]);
    
    setTimeline([
      { year: "2009", title: "Company Founded", description: "Ascot Wealth Management Ltd. (trading as Cape Berkshire Asset Management) was established with a vision to democratize professional investment management." },
      { year: "2012", title: "FCA Authorization", description: "Received full authorization from the Financial Conduct Authority, ensuring regulatory compliance." },
      { year: "2015", title: "£50M Milestone", description: "Reached £50 million in assets under management, serving over 200 satisfied clients." },
      { year: "2018", title: "Technology Innovation", description: "Launched our proprietary portfolio management platform for enhanced client service." },
      { year: "2020", title: "ESG Integration", description: "Introduced ESG-focused investment options across all our model portfolios." },
      { year: "2024", title: "Market Leader", description: "Now managing over £250M for 500+ clients with industry-leading performance." }
    ]);
  };

  const saveContent = async () => {
    try {
      setSaving(true);
      
      const contentUpdates = [
        { key: 'about_header_title', value: headerTitle },
        { key: 'about_header_description', value: headerDescription },
        { key: 'about_story_title', value: storyTitle },
        { key: 'about_story_paragraphs', value: JSON.stringify(storyParagraphs) },
        { key: 'about_values_title', value: valuesTitle },
        { key: 'about_values_description', value: valuesDescription },
        { key: 'about_timeline_title', value: timelineTitle },
        { key: 'about_timeline_description', value: timelineDescription },
        { key: 'about_regulatory_title', value: regulatoryTitle },
        { key: 'about_regulatory_description', value: regulatoryDescription },
        { key: 'about_cta_title', value: ctaTitle },
        { key: 'about_cta_description', value: ctaDescription },
        { key: 'about_stats', value: JSON.stringify(stats) },
        { key: 'about_values', value: JSON.stringify(values) },
        { key: 'about_timeline', value: JSON.stringify(timeline) }
      ];

      // Upsert each content item
      for (const update of contentUpdates) {
        const { error } = await supabase
          .from('service_page_settings')
          .upsert({
            key: update.key,
            value: update.value,
            description: `About page ${update.key.replace('about_', '').replace('_', ' ')}`
          }, {
            onConflict: 'key'
          });

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "About page content updated successfully"
      });
    } catch (error) {
      console.error('Error saving about content:', error);
      toast({
        title: "Error",
        description: "Failed to save about page content",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const addStoryParagraph = () => {
    setStoryParagraphs([...storyParagraphs, ""]);
  };

  const updateStoryParagraph = (index: number, value: string) => {
    const newParagraphs = [...storyParagraphs];
    newParagraphs[index] = value;
    setStoryParagraphs(newParagraphs);
  };

  const removeStoryParagraph = (index: number) => {
    setStoryParagraphs(storyParagraphs.filter((_, i) => i !== index));
  };

  const addStat = () => {
    setStats([...stats, { icon: "Users", value: "", label: "" }]);
  };

  const updateStat = (index: number, field: keyof StatItem, value: string) => {
    const newStats = [...stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setStats(newStats);
  };

  const removeStat = (index: number) => {
    setStats(stats.filter((_, i) => i !== index));
  };

  const addValue = () => {
    setValues([...values, { icon: "Target", title: "", description: "" }]);
  };

  const updateValue = (index: number, field: keyof ValueItem, value: string) => {
    const newValues = [...values];
    newValues[index] = { ...newValues[index], [field]: value };
    setValues(newValues);
  };

  const removeValue = (index: number) => {
    setValues(values.filter((_, i) => i !== index));
  };

  const addTimelineItem = () => {
    setTimeline([...timeline, { year: "", title: "", description: "" }]);
  };

  const updateTimelineItem = (index: number, field: keyof TimelineItem, value: string) => {
    const newTimeline = [...timeline];
    newTimeline[index] = { ...newTimeline[index], [field]: value };
    setTimeline(newTimeline);
  };

  const removeTimelineItem = (index: number) => {
    setTimeline(timeline.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading about page editor...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">About Page Editor</h1>
            <p className="text-muted-foreground mt-2">Edit all content for the About page</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPreviewMode(!previewMode)}
              className="flex items-center gap-2"
            >
              {previewMode ? <Edit3 className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {previewMode ? 'Edit Mode' : 'Preview Mode'}
            </Button>
            <Button onClick={saveContent} disabled={saving} className="flex items-center gap-2">
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {previewMode ? (
          <div className="bg-muted/50 p-4 rounded-lg">
            <Badge className="mb-4">Preview Mode - Changes not visible to public until saved</Badge>
            <div className="space-y-8">
              {/* Preview content would go here - simplified for space */}
              <Card>
                <CardHeader>
                  <CardTitle>{headerTitle}</CardTitle>
                  <CardDescription>{headerDescription}</CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        ) : (
          <Tabs defaultValue="header" className="space-y-6">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="header">Header</TabsTrigger>
              <TabsTrigger value="story">Story</TabsTrigger>
              <TabsTrigger value="stats">Stats</TabsTrigger>
              <TabsTrigger value="values">Values</TabsTrigger>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="cta">Call to Action</TabsTrigger>
            </TabsList>

            <TabsContent value="header" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Header Section</CardTitle>
                  <CardDescription>Main title and description for the about page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="headerTitle">Main Title</Label>
                    <Input
                      id="headerTitle"
                      value={headerTitle}
                      onChange={(e) => setHeaderTitle(e.target.value)}
                      placeholder="About UK Model Portfolios"
                    />
                  </div>
                  <div>
                    <Label htmlFor="headerDescription">Description</Label>
                    <Textarea
                      id="headerDescription"
                      value={headerDescription}
                      onChange={(e) => setHeaderDescription(e.target.value)}
                      placeholder="Brief description of the company..."
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="story" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Our Story Section</CardTitle>
                  <CardDescription>Company background and history</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="storyTitle">Section Title</Label>
                    <Input
                      id="storyTitle"
                      value={storyTitle}
                      onChange={(e) => setStoryTitle(e.target.value)}
                      placeholder="Our Story"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Story Paragraphs</Label>
                      <Button size="sm" onClick={addStoryParagraph} className="flex items-center gap-1">
                        <Plus className="w-4 h-4" />
                        Add Paragraph
                      </Button>
                    </div>
                    {storyParagraphs.map((paragraph, index) => (
                      <div key={index} className="flex gap-2">
                        <Textarea
                          value={paragraph}
                          onChange={(e) => updateStoryParagraph(index, e.target.value)}
                          placeholder={`Paragraph ${index + 1}...`}
                          rows={3}
                          className="flex-1"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeStoryParagraph(index)}
                          className="self-start"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="stats" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Statistics Section</CardTitle>
                  <CardDescription>Company statistics and achievements</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center mb-4">
                    <Label>Statistics</Label>
                    <Button size="sm" onClick={addStat} className="flex items-center gap-1">
                      <Plus className="w-4 h-4" />
                      Add Statistic
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {stats.map((stat, index) => (
                      <Card key={index} className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <Label>Icon Name</Label>
                            <Input
                              value={stat.icon}
                              onChange={(e) => updateStat(index, 'icon', e.target.value)}
                              placeholder="Users, TrendingUp, etc."
                            />
                          </div>
                          <div>
                            <Label>Value</Label>
                            <Input
                              value={stat.value}
                              onChange={(e) => updateStat(index, 'value', e.target.value)}
                              placeholder="500+"
                            />
                          </div>
                          <div>
                            <Label>Label</Label>
                            <Input
                              value={stat.label}
                              onChange={(e) => updateStat(index, 'label', e.target.value)}
                              placeholder="Satisfied Clients"
                            />
                          </div>
                          <div className="flex items-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => removeStat(index)}
                              className="w-full"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="values" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Values Section</CardTitle>
                  <CardDescription>Company values and principles</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="valuesTitle">Section Title</Label>
                    <Input
                      id="valuesTitle"
                      value={valuesTitle}
                      onChange={(e) => setValuesTitle(e.target.value)}
                      placeholder="Our Values"
                    />
                  </div>
                  <div>
                    <Label htmlFor="valuesDescription">Description</Label>
                    <Textarea
                      id="valuesDescription"
                      value={valuesDescription}
                      onChange={(e) => setValuesDescription(e.target.value)}
                      placeholder="Description of the values section..."
                      rows={2}
                    />
                  </div>
                  <Separator />
                  <div className="flex justify-between items-center">
                    <Label>Values List</Label>
                    <Button size="sm" onClick={addValue} className="flex items-center gap-1">
                      <Plus className="w-4 h-4" />
                      Add Value
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {values.map((value, index) => (
                      <Card key={index} className="p-4">
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label>Icon Name</Label>
                              <Input
                                value={value.icon}
                                onChange={(e) => updateValue(index, 'icon', e.target.value)}
                                placeholder="Target, Shield, etc."
                              />
                            </div>
                            <div>
                              <Label>Title</Label>
                              <Input
                                value={value.title}
                                onChange={(e) => updateValue(index, 'title', e.target.value)}
                                placeholder="Client-Focused"
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Textarea
                              value={value.description}
                              onChange={(e) => updateValue(index, 'description', e.target.value)}
                              placeholder="Description of this value..."
                              rows={2}
                            />
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => removeValue(index)}
                            className="w-full"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove Value
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="timeline" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Timeline Section</CardTitle>
                  <CardDescription>Company history and milestones</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="timelineTitle">Section Title</Label>
                      <Input
                        id="timelineTitle"
                        value={timelineTitle}
                        onChange={(e) => setTimelineTitle(e.target.value)}
                        placeholder="Our Journey"
                      />
                    </div>
                    <div>
                      <Label htmlFor="timelineDescription">Description</Label>
                      <Input
                        id="timelineDescription"
                        value={timelineDescription}
                        onChange={(e) => setTimelineDescription(e.target.value)}
                        placeholder="Timeline description..."
                      />
                    </div>
                  </div>
                  <Separator />
                  <div className="flex justify-between items-center">
                    <Label>Timeline Events</Label>
                    <Button size="sm" onClick={addTimelineItem} className="flex items-center gap-1">
                      <Plus className="w-4 h-4" />
                      Add Event
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {timeline.map((item, index) => (
                      <Card key={index} className="p-4">
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label>Year</Label>
                              <Input
                                value={item.year}
                                onChange={(e) => updateTimelineItem(index, 'year', e.target.value)}
                                placeholder="2024"
                              />
                            </div>
                            <div>
                              <Label>Title</Label>
                              <Input
                                value={item.title}
                                onChange={(e) => updateTimelineItem(index, 'title', e.target.value)}
                                placeholder="Company Founded"
                              />
                            </div>
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Textarea
                              value={item.description}
                              onChange={(e) => updateTimelineItem(index, 'description', e.target.value)}
                              placeholder="Description of this milestone..."
                              rows={2}
                            />
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => removeTimelineItem(index)}
                            className="w-full"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove Event
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="cta" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Call to Action Section</CardTitle>
                  <CardDescription>Final section encouraging user engagement</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="ctaTitle">Title</Label>
                    <Input
                      id="ctaTitle"
                      value={ctaTitle}
                      onChange={(e) => setCtaTitle(e.target.value)}
                      placeholder="Ready to Start Your Investment Journey?"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ctaDescription">Description</Label>
                    <Textarea
                      id="ctaDescription"
                      value={ctaDescription}
                      onChange={(e) => setCtaDescription(e.target.value)}
                      placeholder="Encouraging description..."
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </Layout>
  );
};

export default AboutEditor;